import React, { useState, useMemo, useEffect } from "react";
import Layout from "../Layout/Layout";
import GoalsCard from "../Components/GoalsCard";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import {
  useGetGoalsAndTargets,
  useGetReflections,
} from "../Controllers/GoalsAndTargetController";
import { useGetWeeklyWarmapsQuestions } from "../Controllers/Goals/UserGoalController";
import { formatQuarterlyWeek } from "../helpers/DateHelper";

/**
 * localStorage draft scope for Goal Tent / War Map chat (BodyTentChat).
 * Separates goal, goal detail, calendar week, user_goal session, and question (main vs follow-up).
 */
export function buildGoalsTargetsDraftScopeId({
  goalId,
  goalDetailId,
  weekStartIso,
  userGoalId,
  questionId,
  isFollowUp,
}) {
  if (goalId == null || goalDetailId == null || questionId == null) {
    return undefined;
  }
  const week =
    weekStartIso != null ? String(weekStartIso).slice(0, 10) : "week";
  const session = userGoalId ?? "new";
  const qKind = isFollowUp ? "followup" : "main";
  return `goals:g:${goalId}:gd:${goalDetailId}:w:${week}:ug:${session}:q:${questionId}:${qKind}`;
}

/**
 * Draft scope prefix for Weekly War Map (per week + session + step). Use fieldKey per question id in useLocalDraft.
 */
export function buildWeeklyWarmapDraftScopeId({
  weekRangeKey,
  sessionId,
  stepIndex,
}) {
  const week =
    weekRangeKey != null ? String(weekRangeKey).slice(0, 10) : "week";
  const sess = sessionId ?? "session";
  const st = stepIndex ?? 0;
  return `weekly-warmap:w:${week}:s:${sess}:st:${st}`;
}

export default function GoalsAndTargets() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentReflectionUserGoalId, setCurrentReflectionUserGoalId] =
    useState(null);
  const [currentReflectionGoalDetailId, setCurrentReflectionGoalDetailId] =
    useState(null);
  const [currentReflectionGoal, setCurrentReflectionGoal] = useState(null);
  const [startOfWeek, setStartOfWeek] = useState(getStartAndEndOfWeek().start);
  const [activeTabIndex, setActiveTabIndex] = useState(0); // active goal tab
  const [weeklyWarMapLabel, setWeeklyWarMapLabel] = useState("Weekly War Map");
  const [showTooltip, setShowTooltip] = useState(false);

  const apiParams = useMemo(() => {
    const { start, end } = getStartAndEndOfWeek(startOfWeek);
    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    return {
      from: formatDateForAPI(start),
      to: formatDateForAPI(end),
    };
  }, [startOfWeek]);

  const {
    data: weeklyWarmapsQuestions,
    isLoading: isLoadingWeeklyWarmapsQuestions,
    error: weeklyWarmapError,
  } = useGetWeeklyWarmapsQuestions(apiParams);

  const isFutureWeek = () => {
    const today = new Date();
    const currentStart = getStartAndEndOfWeek(today).start;
    return startOfWeek > currentStart;
  };

  useEffect(() => {
    if (weeklyWarmapsQuestions?.session?.is_completed) {
      setWeeklyWarMapLabel("✔ Weekly War Map Completed");
    } else {
      setWeeklyWarMapLabel("Weekly War Map");
    }
  }, [weeklyWarmapsQuestions]);

  useEffect(() => {
    let restored = false;
    if (location.state) {
      if (location.state.weekStart) {
        try {
          setStartOfWeek(new Date(location.state.weekStart));
          restored = true;
        } catch {
          /* ignore invalid date */
        }
      }
      if (typeof location.state.activeTabIndex === "number") {
        setActiveTabIndex(location.state.activeTabIndex);
        restored = true;
      }
    }
    if (!restored) {
      const saved = sessionStorage.getItem("goalsTargetsState");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.weekStart) setStartOfWeek(new Date(parsed.weekStart));
          if (typeof parsed.activeTabIndex === "number")
            setActiveTabIndex(parsed.activeTabIndex);
        } catch {
          /* ignore */
        }
      }
    }
    // Clear the one-time navigation state so back nav won't override new selections later
    if (
      location.state &&
      (location.state.weekStart ||
        typeof location.state.activeTabIndex === "number")
    ) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(
      "goalsTargetsState",
      JSON.stringify({ weekStart: startOfWeek.toISOString(), activeTabIndex }),
    );
  }, [startOfWeek, activeTabIndex]);

  function getStartAndEndOfWeek(baseDate = new Date()) {
    const start = new Date(baseDate);
    const day = start.getDay();
    const daysToSubtract = day === 0 ? 6 : day - 1;
    start.setDate(start.getDate() - daysToSubtract);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  const goToPreviousWeek = () => {
    setStartOfWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setStartOfWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const getWeekDisplayString = () => {
    const { start, end } = getStartAndEndOfWeek(startOfWeek);
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString().slice(-2);
      return `${day}.${month}.${year}`;
    };
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const {
    data: goalsData,
    isLoading: loadingGoals,
    refetch: refetchGoals,
    error: goalsError,
  } = useGetGoalsAndTargets(apiParams);
  const goals = goalsData?.goals || [];

  // Reset active tab if it exceeds available goals
  useEffect(() => {
    if (goals.length > 0 && activeTabIndex >= goals.length) {
      setActiveTabIndex(0);
    }
  }, [goals.length, activeTabIndex]);

  useEffect(() => {
    if (goalsError) {
      toast.error(goalsError.message || "Failed to fetch goals");
      console.error("Failed to fetch goals", goalsError);
    }
  }, [goalsError]);

  const {
    data: reflectionData,
    isLoading: loadingReflection,
    error: reflectionError,
  } = useGetReflections(currentReflectionUserGoalId, {
    enabled: !!currentReflectionUserGoalId,
  });

  const reflections = reflectionData?.Reflection;

  useEffect(() => {
    if (reflectionError) {
      toast.error(reflectionError.message || "Failed to fetch reflection data");
      console.error("Failed to fetch reflection data", reflectionError);
    }
  }, [reflectionError]);

  const handleViewReflection = (userGoalId, goalDetailId) => {
    if (userGoalId === currentReflectionUserGoalId) {
      return;
    }
    setCurrentReflectionUserGoalId(userGoalId);
    setCurrentReflectionGoalDetailId(goalDetailId);

    // Find the goal data for the reflection modal
    const currentGoal = goals.find((goal) =>
      goal.details.some(
        (detail) =>
          detail.user_goals &&
          detail.user_goals.some((userGoal) => userGoal.id === userGoalId),
      ),
    );

    if (currentGoal) {
      setCurrentReflectionGoal({
        goalId: currentGoal.id,
        goalTitle: currentGoal.title,
      });
    }
  };

  const handleGoalUpdate = () => {
    refetchGoals();
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const current = getStartAndEndOfWeek(today).start;
    return current.toDateString() === startOfWeek.toDateString();
  };

  const handleTabClick = (index) => {
    setActiveTabIndex(index);
  };

  return (
    <Layout>
      <div className="page-content">
        <div className="assessment-content goals-main">
          <div className="d-flex justify-content-between flex-column flex-sm-row">
            <h1
              className="text-heading-h2 goals-target-title"
              style={{ color: "#e6ecf1" }}
            >
              Goals and Targets
            </h1>
            <div className="d-flex gap-3">
              <div
                style={{ position: "relative", display: "inline-block" }}
                onMouseEnter={() => isFutureWeek() && setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <button
                  className="btn-1 btn-1-primary"
                  disabled={isFutureWeek()}
                  style={{
                    opacity: isFutureWeek() ? 0.5 : 1,
                    cursor: isFutureWeek() ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (isFutureWeek()) return;
                    navigate("/weekly-warmap", {
                      state: { weeklyWarmapsQuestions },
                    });
                  }}
                >
                  {weeklyWarMapLabel}
                </button>

                {isFutureWeek() && showTooltip && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      padding: "6px 12px",
                      backgroundColor: "rgba(0, 0, 0, 0.9)",
                      color: "#fff",
                      fontSize: "12px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                      pointerEvents: "none",
                    }}
                  >
                    Can't use Weekly War Map for future dates
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                      }}
                    />
                  </div>
                )}
              </div>

              <button
                className="btn-1 btn-1-basic"
                onClick={() => (window.location.href = "goals-overview")}
              >
                Goals Overview
              </button>
            </div>
          </div>
          <div className="goalstarget-container d-flex flex-column align-self-center align-items-center">
            {loadingGoals ? (
              <div className="text-center py-5">
                <div className="loader-container">
                  <div className="loader"></div>
                </div>
              </div>
            ) : (
              <div className="assessment-data w-100 d-flex">
                {goals.length > 0 ? (
                  <>
                    <ul className="nav nav-tabs goals-tabs assessment-custom-tabs gap-0 mb-4">
                      {goals.map((goal, index) => (
                        <li className="nav-item " key={goal.id}>
                          <a
                            className={`goals-name-update nav-link text-body-b1 ${
                              index === activeTabIndex ? "active" : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTabClick(index);
                            }}
                            href="#"
                            title={goal.title}
                          >
                            <span>
                              {goal.title.charAt(0).toUpperCase() +
                                goal.title.slice(1)}
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>

                    <div className="d-flex flex-column align-items-center w-100">
                      <div className="d-flex align-items-center gap-4 mb-2">
                        <button
                          id="prevButton"
                          className="btn d-flex justify-content-center align-items-center rounded-circle"
                          style={{
                            backgroundColor: "#1c2934",
                            width: "40px",
                            height: "40px",
                          }}
                          onClick={goToPreviousWeek}
                        >
                          <img
                            src="/icons/pagination-left-icon.svg"
                            alt="Previous"
                          />
                        </button>
                        <span
                          style={{
                            color: "#ffffff",
                            letterSpacing: "1px",
                            fontSize: "24px",
                          }}
                          className="text-heading-h3 week-date-score"
                        >
                          {getWeekDisplayString()}
                        </span>
                        <button
                          id="nextButton"
                          className="btn d-flex justify-content-center align-items-center rounded-circle"
                          style={{
                            backgroundColor: "#1c2934",
                            width: "40px",
                            height: "40px",
                          }}
                          onClick={goToNextWeek}
                        >
                          <img
                            src="/icons/pagination-right-icon.svg"
                            alt="Next"
                          />
                        </button>
                      </div>
                      <div className="text-center">
                        <span
                          className="text-body-b1"
                          style={{
                            color: "#c8ced3",
                            letterSpacing: "0.5px",
                            fontSize: "16px",
                          }}
                        >
                          {formatQuarterlyWeek(startOfWeek)}
                        </span>
                      </div>
                    </div>

                    <div className="tab-content mt-4 assessment-tab-content d-flex justify-content-center align-items-center">
                      {goals.map((goal, index) => {
                        const tentGoal = goal.details.find(
                          (detail) => detail.type === "tent",
                        );

                        return (
                          <div
                            key={goal.id}
                            className={`tab-pane fade ${
                              index === activeTabIndex ? "show active" : ""
                            }`}
                            style={{
                              display:
                                index === activeTabIndex ? "block" : "none",
                            }}
                          >
                            <div className="d-flex flex-column align-items-center">
                              {!tentGoal ? (
                                <div className="position-relative mb-4">
                                  <button
                                    className="btn-3 btn-1-primary"
                                    onClick={() => {
                                      toast.info(
                                        "No questions available for this goal.",
                                      );
                                    }}
                                  >
                                    {goal.title.charAt(0).toUpperCase() +
                                      goal.title.slice(1)}{" "}
                                    War Map
                                  </button>
                                </div>
                              ) : !tentGoal.is_completed ? (
                                <button
                                  className="btn-3 btn-1-primary mb-4"
                                  onClick={() => {
                                    // if (isCurrentWeek()) {
                                    navigate("/goals-tent", {
                                      state: {
                                        goalId: goal.id,
                                        goalDetailId: tentGoal.id,
                                        today: startOfWeek,
                                        activeTabIndex,
                                        weekStart: startOfWeek.toISOString(),
                                      },
                                    });
                                  }}
                                >
                                  {goal.title.charAt(0).toUpperCase() +
                                    goal.title.slice(1)}{" "}
                                  War Map
                                </button>
                              ) : (
                                <div
                                  className="tent-completed-badge mb-4"
                                  style={{ width: "auto", maxWidth: "unset" }}
                                >
                                  <img
                                    src="/icons/calender-circle-tick.svg"
                                    alt="completed-tick"
                                  />
                                  <p className="text-body-b3 mb-0">
                                    {goal.title.charAt(0).toUpperCase() +
                                      goal.title.slice(1)}{" "}
                                    War Map Completed
                                  </p>
                                </div>
                              )}

                              <div className="row g-4 mb-4">
                                {goal.details
                                  .filter((detail) => detail.type === "target")
                                  .map((detail) => (
                                    <div key={detail.id} className="col-md-6">
                                      <GoalsCard
                                        goalId={goal.id}
                                        goalDetailId={detail.id}
                                        goalTitle={goal.title}
                                        timePeriod={detail.time_period}
                                        isCompleted={detail.is_completed}
                                        userGoal={detail.user_goals[0]}
                                        onUpdate={handleGoalUpdate}
                                        onViewReflection={handleViewReflection}
                                        today={startOfWeek}
                                        activeTabIndex={activeTabIndex}
                                        weekStart={startOfWeek}
                                        // disableBtn={!isCurrentWeek()}
                                        {...detail}
                                      />
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p
                    className="text-center text-body-b2"
                    style={{ color: "#ccc" }}
                  >
                    No goals and target available.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reflection title modal */}
      <div
        className="modal reflection-title-modal fade"
        id="reflectionTitleModal"
        tabIndex="-1"
        aria-labelledby="reflectionTitleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered justify-content-center">
          <div
            className="modal-content"
            style={{ width: "auto", maxWidth: "none " }}
          >
            <div className="modal-header">
              <h1 className="modal-title text-heading-h3">
                {currentReflectionGoal?.goalTitle.charAt(0).toUpperCase() +
                  currentReflectionGoal?.goalTitle.slice(1)}{" "}
                Reflection
              </h1>
            </div>
            <hr className="modal-hr mt-3 mb-4" />

            <div className="modal-body">
              {loadingReflection ? (
                <div className="text-center py-5">
                  <div className="loader-container">
                    <div className="loader"></div>
                  </div>
                </div>
              ) : reflections ? (
                <>
                  <div className="reflection-modal-body">
                    {reflections.main_questions?.map((mainQuestion) => (
                      <div key={mainQuestion.id}>
                        {/* Main Question */}
                        <h1 className="reflection-modal-body-header">
                          {mainQuestion.question}
                        </h1>
                        {/* Main Question Answer */}
                        <p
                          className="text-body-b2 mb-0"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              mainQuestion.answers &&
                                mainQuestion.answers.length > 0
                                ? mainQuestion.answers[0].text
                                : "No answer provided.",
                            ),
                          }}
                        ></p>

                        {/* Follow-up Questions (if any) */}
                        {mainQuestion.follow_up_questions &&
                          mainQuestion.follow_up_questions.length > 0 && (
                            <>
                              {mainQuestion.follow_up_questions.map(
                                (followUp, index) => {
                                  const isFirst = index === 0;
                                  const isLast =
                                    index ===
                                    mainQuestion.follow_up_questions.length - 1;

                                  return (
                                    <div key={followUp.id}>
                                      {/* Top HR only for the first follow-up */}
                                      {isFirst && (
                                        <hr className="modal-hr mt-4 mb-4" />
                                      )}

                                      <h2 className="reflection-modal-body-header">
                                        {followUp.question}
                                      </h2>
                                      <p
                                        className="text-body-b2 mb-0"
                                        dangerouslySetInnerHTML={{
                                          __html: DOMPurify.sanitize(
                                            followUp.answers &&
                                              followUp.answers.length > 0
                                              ? followUp.answers[0].text
                                              : "No answer provided.",
                                          ),
                                        }}
                                      ></p>

                                      {/* Bottom HR for all except last follow-up */}
                                      {!isLast && (
                                        <hr className="modal-hr mt-4 mb-4" />
                                      )}
                                    </div>
                                  );
                                },
                              )}
                            </>
                          )}
                        <hr className="modal-hr mt-4 mb-4" />
                      </div>
                    ))}
                    {reflections.questions?.map((question, index) => (
                      <div key={question.id}>
                        <h1 className="reflection-modal-body-header">
                          {question.question_text}
                        </h1>
                        <p
                          className="text-body-b2 mb-0"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              question.answer && question.answer.length > 0
                                ? question.answer[0].text
                                : "No answer provided.",
                            ),
                          }}
                        ></p>
                        <hr className="modal-hr mt-4 mb-4" />
                      </div>
                    ))}
                  </div>
                  <div className="d-flex gap-3 justify-content-center goals-reflection-btns">
                    <button
                      className="btn-1 btn-1-primary px-3 text-body-b1"
                      style={{ width: "182px" }}
                      data-bs-dismiss="modal"
                      onClick={() => {
                        if (
                          currentReflectionGoal?.goalId &&
                          currentReflectionGoalDetailId
                        ) {
                          navigate("/goals-tent", {
                            state: {
                              goalId: currentReflectionGoal.goalId,
                              goalDetailId: currentReflectionGoalDetailId,
                              today: startOfWeek,
                              activeTabIndex,
                              weekStart: startOfWeek.toISOString(),
                            },
                          });
                        } else {
                          toast.error("Unable to find goal data for editing.");
                        }
                      }}
                    >
                      Edit
                    </button>
                    <button
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      className="btn-1 btn-1-basic px-3 text-body-b1"
                      style={{ width: "166px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="reflection-modal-body">
                  <p className="text-body-b2 mb-0" style={{ color: "#ccc" }}>
                    No reflection data available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
